import React from "react";
import * as Icons from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { SectionHeading } from "@/components/common/SectionHeading";
import { Reveal } from "@/components/common/Reveal";
import { LeadForm } from "@/components/forms/LeadForm";
import { FAQ } from "@/components/blocks/FAQ";

const PLANS = [
  { name: "Starter", for: "For small businesses", icon: "Sprout", features: ["Single communication channel", "Essential automation", "Standard delivery reporting", "Email support"], popular: false },
  { name: "Growth", for: "For growing businesses", icon: "TrendingUp", features: ["Multiple channels (WhatsApp + SMS)", "Advanced automation workflows", "Campaign analytics", "Priority support", "API & webhook access"], popular: true },
  { name: "Enterprise", for: "For larger communication needs", icon: "Building2", features: ["WhatsApp + SMS + Telephony", "Custom automation & routing", "Dedicated onboarding", "SLA-backed support", "Custom integrations"], popular: false },
];

const FAQS = [
  { q: "How is pricing determined?", a: "Pricing is shaped around your communication requirements — the channels you use, expected volumes and the automation you need. Request a quote and we'll tailor it to you." },
  { q: "Can I combine multiple services?", a: "Yes. Many businesses combine WhatsApp, SMS and telephony. We'll recommend the right mix based on your goals." },
  { q: "Is there a plan for small businesses?", a: "Absolutely. The Starter plan is designed for small businesses, and you can scale up as your communication grows." },
];

export default function Pricing() {
  useSEO({
    title: "Pricing & Get a Quote | E Telecom",
    description: "Flexible plans for every business. Get custom pricing for WhatsApp automation, bulk WhatsApp, bulk SMS and smart telephony from E Telecom.",
    path: "/pricing",
  });

  return (
    <>
      <section className="relative overflow-hidden et-hero-wash">
        <div className="absolute inset-0 et-dot-grid opacity-[0.35] pointer-events-none" />
        <div className="et-container relative py-14 lg:py-16 text-center">
          <Reveal>
            <span className="et-eyebrow"><Icons.Tag className="h-3.5 w-3.5" /> Pricing</span>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mt-5">Flexible Plans for Every Business</h1>
            <p className="mt-4 text-slate-600 max-w-2xl mx-auto text-base leading-relaxed">Plans are shaped around your communication requirements. Choose a starting point and get custom pricing based on channels, volume and automation needs.</p>
          </Reveal>
        </div>
      </section>

      {/* Plans */}
      <section className="pb-8 pt-4">
        <div className="et-container">
          <div className="grid lg:grid-cols-3 gap-6 items-stretch">
            {PLANS.map((p, i) => {
              const Icon = Icons[p.icon] || Icons.Package;
              return (
                <Reveal key={p.name} delay={i * 0.06} className="h-full">
                  <div className={`relative h-full rounded-2xl p-7 flex flex-col ${p.popular ? "bg-[var(--et-navy)] text-white shadow-[0_20px_60px_rgba(2,6,23,0.25)]" : "et-card"}`}>
                    {p.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[var(--et-accent)] px-3 py-1 text-[11px] font-bold text-slate-900">Most Popular</span>}
                    <span className={`flex h-12 w-12 items-center justify-center rounded-2xl ${p.popular ? "bg-white/10 text-[var(--et-accent)]" : "bg-[rgba(37,99,235,0.1)] text-[var(--et-primary)]"}`}><Icon className="h-6 w-6" /></span>
                    <h3 className={`font-display text-xl font-bold mt-4 ${p.popular ? "text-white" : "text-slate-900"}`}>{p.name}</h3>
                    <p className={`mt-1 text-sm ${p.popular ? "text-slate-300" : "text-slate-500"}`}>{p.for}</p>
                    <div className={`mt-5 font-display text-2xl font-bold ${p.popular ? "text-[var(--et-accent)]" : "text-[var(--et-primary)]"}`}>Get Custom Pricing</div>
                    <ul className="mt-6 space-y-3 flex-1">
                      {p.features.map((f) => (
                        <li key={f} className={`flex items-start gap-2 text-sm ${p.popular ? "text-slate-200" : "text-slate-600"}`}>
                          <Icons.Check className={`h-4 w-4 mt-0.5 shrink-0 ${p.popular ? "text-[var(--et-accent)]" : "text-[var(--et-primary)]"}`} /> {f}
                        </li>
                      ))}
                    </ul>
                    <a href="#quote" className={`mt-7 inline-flex items-center justify-center gap-2 rounded-xl h-12 px-6 text-sm font-semibold transition-colors ${p.popular ? "bg-[var(--et-accent)] text-slate-900 hover:brightness-95" : "bg-[var(--et-primary)] text-white hover:bg-[var(--et-primary-hover)]"}`}>Request a Quote <Icons.ArrowRight className="h-4 w-4" /></a>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quote form */}
      <section id="quote" className="et-section">
        <div className="et-container">
          <SectionHeading eyebrow="Get a Quote" title="Request Custom Pricing" subtitle="Share a few details and our team will prepare pricing tailored to your communication needs." />
          <div className="mt-10 max-w-2xl mx-auto">
            <Reveal><LeadForm source="quote" testIdPrefix="quote" /></Reveal>
          </div>
        </div>
      </section>

      <FAQ items={FAQS} title="Pricing FAQs" />
    </>
  );
}
